import { createInstance, type BackendModule, type i18n as I18n, type Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';

import { loadNamespace } from '@/locales';
import { SUPPORTED_LOCALES } from '@/shared/config/locales';
import type { AppLocale } from '@/shared/lib';

/** Loads each namespace on first use, as its own chunk, for the active locale only. */
const lazyBackend: BackendModule = {
  type: 'backend',
  init: () => undefined,
  read: (language, namespace, callback) => {
    loadNamespace(language as AppLocale, namespace).then(
      (data) => callback(null, data),
      (error: Error) => callback(error, false),
    );
  },
};

/**
 * Creates the i18next instance for one page load (or one prerendered page).
 *
 * The locale comes from the URL, never from the browser: search engines must
 * see the language the address promises. There is deliberately no fallback
 * language — a fallback would download a second locale on every page — and
 * the locale parity test guarantees there is nothing to fall back for.
 *
 * `preloaded` carries the namespaces a prerendered page embedded, so the
 * first render hydrates without waiting for a request.
 */
export const createI18n = (locale: AppLocale, preloaded: Resource = {}) => {
  const instance = createInstance();
  const ready = instance
    .use(lazyBackend)
    .use(initReactI18next)
    .init({
      lng: locale,
      supportedLngs: [...SUPPORTED_LOCALES],
      fallbackLng: false,
      ns: ['common'],
      defaultNS: 'common',
      resources: preloaded,
      partialBundledLanguages: true,
      // Synchronous init: a hydrating page renders its embedded strings at once.
      initAsync: false,
      // React escapes what it renders; escaping here would double-encode the
      // apostrophes Ukrainian copy is full of.
      interpolation: { escapeValue: false },
      returnNull: false,
    });
  return { i18n: instance as I18n, ready };
};
